/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { notify } from '../../common/functions/react-toastify.function';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { handleApiError } from '../../common/functions/errorHandler.function';
import { useAtom } from 'jotai';
import {
  openReviewForFile,
  openUploadFileRanDb,
  ranDbFileUploadResponse,
} from '../../common/atom/atoms';
import { uploadRanDbExcelFile } from './functions/upload_ran_db_excel_file.function';
import { uploadRanDbExcelFileWithProgress } from './functions/upload_ran_db_excel_file_with_progress.function';
import { uploadFiberSitePlanningExcel } from '../fiberSitePlanning/functions/upload_fiber_site_planning_excel.function';

export default function UploadRanDBFileModal({ fetchUsers }) {
  const { pathname } = useLocation();
  // Use FTTS-only upload API when on FTTS Site List (/oko/fiberSite or fiberSitePlanning)
  const isFiberSitePage = pathname.includes('fiberSite') || pathname.includes('fiberSitePlanning');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [open, setOpen] = useAtom(openUploadFileRanDb);
  const [ranDbfileResponse, setFileResponse] = useAtom(ranDbFileUploadResponse);
  const [fileReview, setFileReview] = useAtom(openReviewForFile);
  const missingColumns = ranDbfileResponse?.columnMapping?.missingColumns || [];

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const validExtensions = ['.xls', '.xlsx'];

    // Clear existing state when a new file is selected
    setFileResponse(null);
    setSelectedFile(null);

    if (file && validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      setSelectedFile(file);
      handleUpload(file);
    } else {
      alert('Please upload a valid Excel file (.xls or .xlsx).');
      setSelectedFile(null);
    }
  };
  useEffect(() => {
    if (selectedFile) {
      handleUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);
  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadStage('Starting...');
    setUploadMessage('');
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      if (isFiberSitePage) {
        const { result } = await uploadFiberSitePlanningExcel(
          formData,
          ({ progress, stage, message }) => {
            setUploadProgress(typeof progress === 'number' ? progress : 0);
            setUploadStage(stage || '');
            setUploadMessage(message || '');
          }
        );
        setFileResponse(result);
        setUploadProgress(100);
        notify('success', 'File uploaded successfully (FTTS comparison)');
      } else {
        const { result } = await uploadRanDbExcelFileWithProgress(
          formData,
          ({ progress, stage, message }) => {
            setUploadProgress(typeof progress === 'number' ? progress : 0);
            setUploadStage(stage || '');
            setUploadMessage(message || '');
          }
        );
        setFileResponse(result);
        setUploadProgress(100);
        notify('success', 'File uploaded successfully');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setUploading(false);
    }
  };
  const handleClose = () => {
    setOpen(false);
    setFileResponse(null);
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStage('');
    setUploadMessage('');
  };
  const handleRemoveFile = () => {
    setSelectedFile(null);
  };
  const handleOpenReview = () => {
    setFileReview(true);
  };
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        // Don't allow closing on backdrop click or ESC (prevents losing in-flight upload state)
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Upload</span>
        <IconButton onClick={handleClose} disabled={uploading} aria-label="Close upload modal">
          <CancelIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: '4px',
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
            position: 'relative',
            backgroundColor: '#f9f9f9',
          }}
          onClick={() => document.getElementById('fileInput').click()}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              margin: '0 auto',
              backgroundColor: '#EBB9BB',
              borderRadius: '20%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
            }}
          >
            <AddIcon sx={{ fontSize: 32, color: '#BD1D23' }} />
          </Box>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Click or drag file to this area to upload
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Download format from{' '}
            <Box
              component="a"
              href="/path/to/sample.csv" // replace with your actual CSV path
              download
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{
                color: '#2196F3',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
                cursor: 'pointer',
              }}
            >
              here
            </Box>
          </Typography>

          <input
            id="fileInput"
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Box>

        {selectedFile && (
          <>
            <Box
              sx={{
                mt: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #ccc',
                background: 'rgba(0, 0, 0, 0.05)',
                borderRadius: '4px',
              }}
            >
              <Typography>{selectedFile.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {uploading && !isFiberSitePage && uploadProgress === 0 ? (
                  <CircularProgress size={18} />
                ) : !uploading ? (
                  <IconButton onClick={handleRemoveFile}>
                    <CancelIcon />
                  </IconButton>
                ) : null}
              </Box>
            </Box>
            {uploading && (
              <Box sx={{ mt: 2, minHeight: 56 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight={500} color="text.secondary">
                    {uploadStage || 'Processing...'}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      color: (() => {
                        const p = Math.min(100, Math.max(0, Number(uploadProgress) || 0));
                        if (p < 40) {
                          return '#1976d2';
                        }
                        if (p < 70) {
                          return '#0288d1';
                        }
                        if (p < 100) {
                          return '#00897b';
                        }
                        return '#2e7d32';
                      })(),
                      transition: 'color 0.4s ease',
                    }}
                  >
                    {Math.min(100, Math.max(0, Number(uploadProgress) || 0))}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    height: 12,
                    borderRadius: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)',
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(100, Math.max(0, Number(uploadProgress) || 0))}%`,
                      borderRadius: 2,
                      background:
                        'linear-gradient(90deg, #1976d2 0%, #0288d1 35%, #00897b 70%, #2e7d32 100%)',
                      backgroundSize: '200% 100%',
                      boxShadow: '0 0 12px rgba(0, 150, 136, 0.35)',
                      transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      animation: 'progressShine 2s ease-in-out infinite',
                      '@keyframes progressShine': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.92 },
                      },
                    }}
                  />
                </Box>
                {uploadMessage && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.75 }}
                  >
                    {uploadMessage}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
        {ranDbfileResponse && selectedFile && (
          <>
            {Array.isArray(missingColumns) && missingColumns.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  backgroundColor: 'rgba(245, 158, 11, 0.08)',
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                  Some columns are missing from your upload
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.7)' }}>
                  Missing columns will not overwrite existing database values. Only the columns
                  present in the uploaded file (and any detected changes) will be updated.
                </Typography>
                <Typography sx={{ fontSize: 12, mt: 0.75, color: 'rgba(0,0,0,0.7)' }}>
                  Missing: {missingColumns.slice(0, 12).join(', ')}
                  {missingColumns.length > 12 ? ` (+${missingColumns.length - 12} more)` : ''}
                </Typography>
              </Box>
            )}
            <div
              style={{
                width: '100%',
                height: '100%',
                padding: 10,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <div
                style={{
                  flex: '1 1 0',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 82,
                  display: 'flex',
                }}
              >
                <div
                  style={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 8,
                    display: 'flex',
                  }}
                >
                  <div style={{ width: 20, height: 20, position: 'relative' }}>
                    <WarningAmberIcon sx={{ fontSize: 20, color: '#F59E0B' }} />
                  </div>
                  <div
                    style={{
                      textBoxTrim: 'trim-both',
                      textBoxEdge: 'cap alphabetic',
                      color: 'black',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '400',
                      wordWrap: 'break-word',
                    }}
                  >
                    {ranDbfileResponse?.newSites?.length} new sites found
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                width: '100%',
                height: '100%',
                padding: 10,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <div
                style={{
                  flex: '1 1 0',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 82,
                  display: 'flex',
                }}
              >
                <div
                  style={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 8,
                    display: 'flex',
                  }}
                >
                  <div style={{ width: 20, height: 20, position: 'relative' }}>
                    <WarningAmberIcon sx={{ fontSize: 20, color: '#F59E0B' }} />
                  </div>
                  <div
                    style={{
                      textBoxTrim: 'trim-both',
                      textBoxEdge: 'cap alphabetic',
                      color: 'black',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '400',
                      wordWrap: 'break-word',
                    }}
                  >
                    {ranDbfileResponse?.updatedSites?.length} sites with updated record
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                width: '100%',
                height: '100%',
                padding: 10,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <div
                style={{
                  flex: '1 1 0',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 82,
                  display: 'flex',
                }}
              >
                <div
                  style={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 8,
                    display: 'flex',
                  }}
                >
                  <div style={{ width: 20, height: 20, position: 'relative' }}>
                    <WarningAmberIcon sx={{ fontSize: 20, color: '#F59E0B' }} />
                  </div>
                  <div
                    style={{
                      textBoxTrim: 'trim-both',
                      textBoxEdge: 'cap alphabetic',
                      color: 'black',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '400',
                      wordWrap: 'break-word',
                    }}
                  >
                    {ranDbfileResponse?.deletedSites?.length} sites missing
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleOpenReview}
          variant="contained"
          sx={{
            backgroundColor: '#BD1D23',
            width: '92%',
            marginRight: '13px',
          }}
          disabled={!selectedFile || uploading}
        >
          Review
        </Button>
      </DialogActions>
    </Dialog>
  );
}
